// @tailwind
import { Box, Theme } from '@mui/material';
import DDMNavBar from '@/externals/ddm/components/DDMNavBar';
import MenuDrawer, { MenuItem } from '@/layouts/StudioLayout/MenuDrawer';
import { useState, useRef, useEffect } from 'react';
import {
    Outlet,
    useParams,
    ScrollRestoration,
    useNavigation,
    useRevalidator,
    useLoaderData,
    useLocation,
} from 'react-router-dom';
import {
    PhonelinkSetup as SpaceIcon,
    NewReleases,
    QueryStats as QueryStatsIcon,
    AdsClick as AdsClickIcon,
    ManageAccountsOutlined as ManageAccountsOutlinedIcon,
    Dns as DnsIcon,
} from '@mui/icons-material';
import { env } from '@/constant/configure';
import { useTranslation } from 'react-i18next';

import { useSessionStore, useSpaceUserV2Store } from '@/store';
import PageLoadingScreen from '@/components/PageLoadingScreen';
import { DialogContainer, dialogClear, dialogClose, dialogOpen } from '@/modules/MuiDialog';
import MarketingTermsDialog from '@/components/MarketingTermsDialog';
import { postConfirmTerms } from '@/services/auth.service';
import Banner from '../components/Banner';
import { toast } from 'react-toastify';
import makeErrorString from '@/utils/makeErrorString';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LiveReg from '@/assets/images/live/menu1.png';
import LiveSchedule from '@/assets/images/live/menu2.png';
import LiveLink from '@/assets/images/live/menu3.png';

const StudioLayout = () => {
    const { t } = useTranslation();
    const { spaceUserData = {} } = useLoaderData() as any;
    const revalidate = useRevalidator();
    const [appBarHeight, setAppBarHeight] = useState(64);
    const [drawWidth, setDrawWidth] = useState(240);
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);
    const [isBannerOpen, setIsBannerOpen] = useState(false);
    const drawRef = useRef<HTMLDivElement>(null);
    const params = useParams();

    const session = useSessionStore();
    const userId = params.userId || session.userId || '';
    const location = useLocation();
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';

    const { state } = useNavigation();

    // 뷰포트 크기에 따라 Drawer 상태 자동 조정
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 600; // sm breakpoint
            setIsDrawerOpen(!isMobile);
        };

        // 초기 실행
        handleResize();

        // 리사이즈 이벤트 리스너 등록
        window.addEventListener('resize', handleResize);

        // 클린업
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 소도몰 사용자 색상 설정
    useEffect(() => {
        if (isSodomall) {
            const root = document.documentElement;
            // primary 색상 (#363bc2 = rgb(54, 59, 194))
            root.style.setProperty('--v-palette-primary-main', '#363bc2');
            root.style.setProperty('--v-palette-primary-light', '#5a5fd4');
            root.style.setProperty('--v-palette-primary-dark', '#2a2d8a');
            root.style.setProperty('--v-palette-primary-contrastText', '#fff');
            root.style.setProperty('--v-palette-primary-mainChannel', '54 59 194');
            root.style.setProperty('--v-palette-primary-lightChannel', '90 95 212');
            root.style.setProperty('--v-palette-primary-darkChannel', '42 45 138');
        }
    }, [isSodomall]);

    // 라이브 관련 메뉴 (Index 0, 1)
    const liveMenuItems: MenuItem[] = [
        {
            label: t('studio.liveScheduleCreate'),
            icon: (
                <Box
                    component="img"
                    src={LiveReg}
                    sx={{
                        width: 24,
                        height: 24,
                    }}
                />
            ),
            to: `/studio/${userId || '-'}/live/schedule-reg`,
        },
        {
            label: t('studio.liveScheduleList'),
            icon: (
                <Box
                    component="img"
                    src={LiveSchedule}
                    sx={{
                        width: 24,
                        height: 24,
                    }}
                />
            ),
            to: `/studio/${userId || '-'}/live/schedule-list`,
        },
    ];

    // 프로필 관련 메뉴 (Index 2)
    const profileMenuItems: MenuItem[] = [
        {
            label: t('studio.profileLinkManagementMenu'),
            icon: (
                <Box
                    component="img"
                    src={LiveLink}
                    sx={{
                        width: 24,
                        height: 24,
                    }}
                />
            ),
            to: `/studio/${userId || '-'}`,
        },
    ];

    // 경로에 따라 메뉴 결정
    const getMenuByPath = () => {
        const currentPath = location.pathname;

        // /studio/:userId/live로 시작하는 경로
        if (currentPath.startsWith(`/studio/${userId}/live`)) {
            return liveMenuItems;
        }

        // /studio/:userId 또는 /studio/:userId/로 끝나는 경로, 또는 다른 경로들 (pick, user-setting 등)
        return profileMenuItems;
    };

    const menu = getMenuByPath();
    const menu_dev: MenuItem[] = [
        ...menu,
        // {
        //     label: 'Template Demo',
        //     icon: <TemplateIcon />,
        //     isActive: false,
        //     children: [
        //         {
        //             label: 'Typography',
        //             to: '/template-demo',
        //         },
        //         {
        //             label: 'Toast Editor',
        //             to: '/template-demo/toast-editor',
        //         },
        //         {
        //             label: 'Dialogs',
        //             to: '/template-demo/dialog',
        //         },
        //         {
        //             label: 'Buttons',
        //             to: '/template-demo/button',
        //         },
        //         {
        //             label: 'Custom Fields',
        //             to: '/template-demo/custom-fields',
        //         },
        //         {
        //             label: 'Drag and Drop',
        //             to: '/template-demo/drag-and-drop',
        //         },
        //     ],
        // },
    ];

    useEffect(() => {
        if (!!drawRef.current) {
            setDrawWidth(!!drawRef?.current ? drawRef?.current?.clientWidth : 0);
        }
    });
    useEffect(() => {
        if (!!spaceUserData?.is_mall_opened) return;
        dialogOpen({
            innerElement: (
                <MarketingTermsDialog
                    onConfirmed={async (result) => {
                        try {
                            // console.log('result::::', result)
                            await postConfirmTerms(userId, {
                                emailMarketingConsented: result,
                                smsMarketingConsented: result,
                            });
                            revalidate.revalidate();
                            dialogClose('marketing-confirm');
                            dialogClear();
                        } catch (error) {
                            toast.error(makeErrorString(error, '잠시 후 다시 시도해주세요.'), {
                                toastId: 'reject-open-mall',
                            });
                            throw error;
                        } finally {
                            dialogClear();
                        }
                    }}
                />
            ),
            disableBackdropClick: true,
            maxWidth: 'xs',
            id: 'marketing-confirm',
        });
    }, [spaceUserData?.is_mall_opened]);

    return (
        <>
            <PageLoadingScreen isLoading={state === 'loading'} />
            <Box
                className={'flex flex-col'}
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    '--app-bar-height': `${appBarHeight}px`,
                    scrollBehavior: 'smooth',
                }}
            >
                <DDMNavBar setAppBarHeight={setAppBarHeight} />
                {menu.length > 0 && (
                    <MenuDrawer
                        isDrawerOpen={isDrawerOpen}
                        appBarHeight={appBarHeight}
                        ref={drawRef}
                        // append={<AppendExample/>}
                        menu={env === 'prd' ? menu : menu_dev}
                    />
                )}
                <Box
                    sx={(theme: Theme) => ({
                        flexGrow: 1,
                        // backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        // marginLeft: isDrawerOpen ? `${drawWidth}px` : 0,
                        transition: `margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms`,
                        display: 'flex',
                        flexDirection: 'column',

                        [theme.breakpoints.up('sm')]: {
                            marginLeft: menu.length > 0 && isDrawerOpen ? `${drawWidth}px` : 0,
                        },
                    })}
                >
                    <Banner
                        isOpen={isBannerOpen}
                        color={'warning'}
                        prepend={
                            <NewReleases
                                sx={{
                                    fontSize: '45px',
                                }}
                            />
                        }
                        message={
                            '현재 회원님은 관리자로부터 최종 승인이 되지 않았습니다.<br/>일부 기능의 사용이 제한될 수 있습니다.'
                        }
                        isAllowHtml
                        closeText={'네, 확인했습니다'}
                        ButtonProps={{
                            variant: 'outlined',
                            color: 'black',
                        }}
                        appBarHeight={appBarHeight}
                        sticky
                    />
                    <Box
                        component={'section'}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            px: 3,
                            py: 2,
                            flexGrow: 1,
                        }}
                    >
                        <Outlet />
                        <ScrollRestoration
                            getKey={(location, matches) => {
                                return location.key;
                            }}
                        />
                    </Box>
                    {/* <Footer component={'footer'} px={3} py={1} /> */}
                </Box>
            </Box>
            <DialogContainer />
        </>
    );
};

export default StudioLayout;
